import { Formik } from 'formik';
import { TextField, Box, Button, Divider, Grid } from 'components/ui';
import { WideForm } from 'components/styles_components/form';
import { FormattedMessage, useIntl } from 'react-intl';
import { FormHintTypography, TitleTypography } from 'components/styles_components/typography';
import PlaceAutocomplete from 'components/form/autocomplete/place';
import InspectionSubjectAutocomplete from 'components/form/autocomplete/inspection_subject';
import UserAutocomplete from 'components/form/autocomplete/user';
import PeriodSelect from 'components/form/period_select';
import DatePicker from 'components/form/date_picker';
import DragAndDropField from 'components/inspection_field/drag_and_drop_field';
import validationSchema from 'validation/inspection/create';

const Form = ({ initialValues, processing, onSubmit, onCancel }) => {
  const intl = useIntl();

  const handleSubmit = (values) => {
    values.inspectionFields = (values.inspectionFields || []).map((field, index) => ({
      ...field,
      order: index,
    }));
    onSubmit(values);
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema(intl)}
      onSubmit={handleSubmit}
    >
      {(formik) => (
        <WideForm onSubmit={formik.handleSubmit}>
          <PlaceAutocomplete
            autoFocus
            margin="dense"
            id="placeId"
            name="placeId"
            label={intl.formatMessage({ id: 'place' }) + ' *'}
            value={formik.values.placeId}
            onChange={formik.handleChange}
            setValue={formik.setFieldValue}
            onBlur={formik.handleBlur}
            error={(formik.touched.placeId && Boolean(formik.errors.placeId)) || undefined}
            helperText={formik.touched.placeId && formik.errors.placeId}
          />
          <TextField
            fullWidth
            margin="dense"
            id="name"
            name="name"
            label={intl.formatMessage({ id: 'form.inspection.input.name' }) + ' *'}
            value={formik.values.name}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={(formik.touched.name && Boolean(formik.errors.name)) || undefined}
            helperText={formik.touched.name && formik.errors.name}
          />
          <InspectionSubjectAutocomplete
            margin="dense"
            id="inspectionSubjectId"
            name="inspectionSubjectId"
            setValue={formik.setFieldValue}
            label={intl.formatMessage({ id: 'inspection_subject' })}
            placeId={formik.values.placeId}
            value={formik.values.inspectionSubjectId}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={
              (formik.touched.inspectionSubjectId && Boolean(formik.errors.inspectionSubjectId)) ||
              undefined
            }
            helperText={formik.touched.inspectionSubjectId && formik.errors.inspectionSubjectId}
          />
          <UserAutocomplete
            margin="dense"
            id="inspectorId"
            name="inspectorId"
            placeId={formik.values.placeId}
            setValue={formik.setFieldValue}
            label={intl.formatMessage({ id: 'inspector' })}
            value={formik.values.inspectorId}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={(formik.touched.inspectorId && Boolean(formik.errors.inspectorId)) || undefined}
            helperText={formik.touched.inspectorId && formik.errors.inspectorId}
          />

          <Divider />
          <TitleTypography>
            <FormattedMessage id="form.inspection.section.when" />
          </TitleTypography>
          <DatePicker
            className="full-width"
            name="dueDate"
            id="dueDate"
            label={intl.formatMessage({ id: 'form.inspection.input.due_date' })}
            value={formik.values.dueDate}
            onChange={(value) => formik.setFieldValue('dueDate', value)}
          />
          <FormHintTypography>
            <FormattedMessage id="form.inspection.due_date.hint" />
          </FormHintTypography>

          <Divider />
          <TitleTypography>
            <FormattedMessage id="form.inspection.section.frequency" />
          </TitleTypography>
          <Grid container columnSpacing={2}>
            <Grid size={{ xs: 6, md: 3 }}>
              <TextField
                fullWidth
                margin="dense"
                type="number"
                id="periodNumber"
                name="periodNumber"
                slotProps={{ htmlInput: { min: 1 } }}
                label={intl.formatMessage({ id: 'form.inspection.input.period_number' }) + ' *'}
                value={formik.values.periodNumber}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  (formik.touched.periodNumber && Boolean(formik.errors.periodNumber)) || undefined
                }
                helperText={formik.touched.periodNumber && formik.errors.periodNumber}
              />
            </Grid>
            <Grid size={{ xs: 6, md: 9 }}>
              <PeriodSelect
                fullWidth
                margin="dense"
                id="periodCycle"
                name="periodCycle"
                label={intl.formatMessage({ id: 'form.inspection.input.period_cycle' }) + ' *'}
                value={formik.values.periodCycle}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  (formik.touched.periodCycle && Boolean(formik.errors.periodCycle)) || undefined
                }
              />
            </Grid>
          </Grid>
          <FormHintTypography>
            <FormattedMessage id="form.inspection.period.hint" />
          </FormHintTypography>

          <Divider />
          <TitleTypography>
            <FormattedMessage id="stepper.check_list" />
          </TitleTypography>
          <DragAndDropField
            name="inspectionFields"
            formik={formik}
            showValue={false}
            processing={processing}
          />

          <Divider />
          <TitleTypography>
            <FormattedMessage id="form.inspection.section.notes" />
          </TitleTypography>
          <TextField
            fullWidth
            margin="dense"
            multiline
            rows={4}
            id="notes"
            name="notes"
            label={intl.formatMessage({ id: 'form.inspection.input.notes' })}
            value={formik.values.notes}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={(formik.touched.notes && Boolean(formik.errors.notes)) || undefined}
            helperText={formik.touched.notes && formik.errors.notes}
          />

          <Box display="flex" justifyContent="space-between" className="margin-top-10">
            <Button variant="text" size="small" disabled={processing} onClick={onCancel}>
              <FormattedMessage id="button.cancel" />
            </Button>
            <Button
              variant="contained"
              color="primary"
              type="submit"
              disabled={processing || !formik.isValid}
            >
              <FormattedMessage id="button.save" />
            </Button>
          </Box>
        </WideForm>
      )}
    </Formik>
  );
};

export default Form;
